import React from 'react'
import styled from 'styled-components'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import Divider from 'LuxKit/Divider'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import { BookingProtectionContent } from './BookingProtectionComparison'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'

const Wrapper = styled.div`
  padding: ${rem(16)} ${rem(20)};
  position: relative;
  border: 1px solid ${(props) => props.theme.palette.highlight.secondary.normalForeground};
`

const StyledPromotionLabel = styled(PromotionLabel)`
  position: absolute;
  top: -${rem(12)};
  right: ${rem(16)};
`

const CardHeader = styled(Group)`
  height: ${rem(62)};
`

const EnhancedProtectionRow = styled(Group)`
  padding: ${rem(8)} 0;
`

interface Props {
  bookingProtectionContent: Array<BookingProtectionContent>
  bookingProtectionQuotePricePerTraveller?: number;
  bookingProtectionSelected?: boolean;
  bookingProtectionQuotePrice: number;
  checkoutWithDiscountedBookingProtection: boolean;
  bookingProtectionQuoteLuxPlusPricePerTraveller: number;
  bookingProtectionQuoteLuxPlusPrice: number;
  isStandaloneExperience: boolean;
  onAddCFMRBookingProtection: () => void;
  onAddBookingProtection: () => void;
  quote?: App.InsuranceQuote;
}

function BookingEnhancedProtectionCard(props: Props) {
  const {
    bookingProtectionContent,
    bookingProtectionQuotePricePerTraveller = 0,
    bookingProtectionSelected,
    bookingProtectionQuotePrice,
    checkoutWithDiscountedBookingProtection,
    bookingProtectionQuoteLuxPlusPricePerTraveller,
    bookingProtectionQuoteLuxPlusPrice,
    isStandaloneExperience,
    onAddCFMRBookingProtection,
    onAddBookingProtection,
    quote,
  } = props

  return (
    <Wrapper>
      <StyledPromotionLabel variant="premium">
        Recommended
      </StyledPromotionLabel>

      <Group gap={12} direction="vertical">
        <CardHeader gap={4} direction="vertical">
          <BodyText colour="highlight-secondary"variant="medium" weight="bold">
            Cancellation Protection terms
          </BodyText>
          <Group direction="vertical">
            {checkoutWithDiscountedBookingProtection && (
              <>
                {quote?.productId === 'CFMR' && (
                  <PriceRowLuxPlusPrice
                    memberPrice={quote.luxPlusTotalPerPerson}
                    saleUnit="person"
                    size="M"
                    priceFormat="dynamicDollar"
                  />
                )}

                {!isStandaloneExperience && quote?.productId !== 'CFMR' && (
                  <PriceRowLuxPlusPrice
                    memberPrice={bookingProtectionQuoteLuxPlusPricePerTraveller}
                    saleUnit="person"
                    size="M"
                    priceFormat="dynamicDollar"
                  />
                )}

                {isStandaloneExperience && quote?.productId !== 'CFMR' && (
                  <PriceRowLuxPlusPrice
                    memberPrice={bookingProtectionQuoteLuxPlusPrice}
                    saleUnit="total"
                    size="M"
                    priceFormat="dynamicDollar"
                  />
                )}
              </>
            )}
            {!checkoutWithDiscountedBookingProtection && (
              <>
                {quote?.productId === 'CFMR' && (
                  <Heading variant="heading5">
                    <FormatCurrency value={quote.totalPerPerson} />
                    <Caption variant="large" as="span">{' '}/person</Caption>
                  </Heading>
                )}
                {!isStandaloneExperience && quote?.productId !== 'CFMR' && (
                  <Heading variant="heading5">
                    <FormatCurrency value={bookingProtectionQuotePricePerTraveller} />
                    <Caption variant="large" as="span">{' '}/person</Caption>
                  </Heading>
                )}
                {isStandaloneExperience && quote?.productId !== 'CFMR' && (
                  <Heading variant="heading5">
                    <FormatCurrency value={bookingProtectionQuotePrice} />
                    <Caption variant="large" as="span">{' '}/total</Caption>
                  </Heading>
                )}
              </>
            )}
          </Group>
        </CardHeader>

        <Group direction="vertical">
          {bookingProtectionContent.map((item, index) => (
            <Group direction="vertical" key={index}>
              {index === 0 && <Divider kind="primary" />}
              <EnhancedProtectionRow direction="vertical" gap={8}>
                <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                  <BodyText variant="small" weight="bold">{item.heading}</BodyText>
                  {item.enhancedProtectionIcon}
                </Group>
                <BodyText variant="small">{item.enhancedProtectionText}</BodyText>
              </EnhancedProtectionRow>
              {index !== bookingProtectionContent.length - 1 && <Divider kind="primary" />}
            </Group>
          ))}

          <TextButton kind="secondary" onClick={quote?.productId === 'CFMR' ? onAddCFMRBookingProtection : onAddBookingProtection} isSelected={!!bookingProtectionSelected}>
            {bookingProtectionSelected ? 'Added' : 'Add protection'}
          </TextButton>
        </Group>
      </Group>
    </Wrapper>
  )
}

export default BookingEnhancedProtectionCard
